import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Settings, Bell, Globe, LogOut, Camera, Loader2, Pencil, X, Shield } from "lucide-react";
import { useAuth, useIsAdmin } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { sanitizeError } from "@/lib/errorHandler";
import { invokeWithAuth } from "@/lib/supabaseHelpers";
import { toast } from "sonner";

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  onAvatarChange?: (url: string) => void;
}

const ProfileDrawer = ({ open, onClose, onAvatarChange }: ProfileDrawerProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: isAdmin } = useIsAdmin();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user && open) {
      fetchProfile();
    }
  }, [user, open]);

  const fetchProfile = async () => {
    if (!user) return;
    const googleAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture;
    const googleName = user.user_metadata?.full_name || user.user_metadata?.name;
    const { data } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", user.id)
      .single();
    if (data) {
      setDisplayName(data.display_name || googleName || "");
      setAvatarUrl(data.avatar_url || googleAvatar || null);
    } else {
      setDisplayName(googleName || "");
      setAvatarUrl(googleAvatar || null);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const ext = file.name.toLowerCase().split('.').pop() || '';
    if (!['jpg','jpeg','png','gif','webp','avif'].includes(ext)) {
      toast.error("Use: jpg, png, gif, webp ou avif");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'avatars');
      formData.append('fileType', 'image');
      const { data, error } = await invokeWithAuth<{ success: boolean; url: string; error?: string }>('validate-upload', { body: formData });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro no upload');
      const newUrl = `${data.url}?t=${Date.now()}`;
      const { error: updateError } = await supabase.from("profiles").update({ avatar_url: newUrl }).eq("user_id", user.id);
      if (updateError) {
        // Profile might not exist yet, try insert
        const { error: insertError } = await supabase.from("profiles").insert({ user_id: user.id, avatar_url: newUrl });
        if (insertError) throw insertError;
      }
      setAvatarUrl(newUrl);
      onAvatarChange?.(newUrl);
      toast.success("Foto atualizada!");
    } catch (err) {
      toast.error(sanitizeError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onClose();
    navigate("/");
  };

  const handleNav = (path: string) => {
    onClose();
    navigate(path);
  };

  const getUserInitials = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  const menuItems = [
    { icon: User, label: "Meu Perfil", path: "/perfil" },
    { icon: Pencil, label: "Editar Perfil", path: "/editar-perfil" },
    { icon: Settings, label: "Configurações", path: "/configuracoes" },
    { icon: Bell, label: "Notificações", path: "/notificacoes" },
    { icon: Globe, label: "Idioma", path: "/idioma" },
    ...(isAdmin ? [{ icon: Shield, label: "Administração", path: "/admin" }] : []),
  ];

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-[70] w-[85%] max-w-[360px] bg-card shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ borderRadius: "12px 0 0 12px" }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* User info */}
        <div className="pt-10 pb-6 px-6 flex flex-col items-center gap-3">
          <div className="relative">
            <Avatar className="h-20 w-20 border-4 border-border shadow-md">
              <AvatarImage src={avatarUrl || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <label
              htmlFor="drawer-avatar-upload"
              className="absolute bottom-0 right-0 p-1.5 bg-primary text-primary-foreground rounded-full cursor-pointer hover:bg-primary/90 transition-colors shadow-md"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
            </label>
            <input
              id="drawer-avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              disabled={uploading}
            />
          </div>
          <div className="text-center">
            <p className="font-semibold text-foreground text-lg">
              {displayName || "Usuário"}
            </p>
            <p className="text-sm text-muted-foreground truncate max-w-[250px]">
              {user?.email}
            </p>
          </div>
        </div>

        <Separator />

        {/* Menu items */}
        <nav className="py-3 px-3">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground hover:bg-muted/80 transition-all duration-200 group"
            >
              <item.icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <Separator />

        {/* Logout */}
        <div className="px-3 py-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-destructive hover:bg-destructive/10 transition-all duration-200"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-medium">Sair</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default ProfileDrawer;
